import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import {
  BookOpen, Globe2, MapPin, Network, PackageCheck, StickyNote, AlertTriangle,
  ArrowLeft, Link as LinkIcon,
} from "lucide-react";

export default function LanguageDetail() {
  const { iso } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/projects/language-detail/${encodeURIComponent(iso)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [iso]);

  if (loading) return <div className="p-10 mono text-xs uppercase tracking-widest">Loading…</div>;
  if (err) return <div className="p-10 text-sm text-[color:var(--behind)]">{err}</div>;

  const { language_name, engagements, notes, issues, project } = data;
  const status = (engagements[0]?.statuses || [])[0];

  return (
    <div className="max-w-[1500px] mx-auto px-10 py-10 fade-in-up" data-testid="language-detail">
      <Link to="/projects" className="text-xs text-[color:var(--ink-2)] hover:text-[color:var(--ink)] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to Projects
      </Link>
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Language Detail · ISO {iso}
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">{language_name || iso}</h1>
        <div className="flex flex-wrap gap-3 mt-3 text-sm">
          <span className="mono text-[color:var(--ink-2)]">{engagements.length} engagement{engagements.length === 1 ? "" : "s"}</span>
          <span className="mono text-[color:var(--ink-2)]">·</span>
          <span className="mono text-[color:var(--ink-2)]">{new Set(engagements.map((e) => e.country).filter(Boolean)).size} countr{new Set(engagements.map((e) => e.country)).size === 1 ? "y" : "ies"}</span>
          {project && (
            <>
              <span className="mono text-[color:var(--ink-2)]">·</span>
              <span className="mono text-[color:var(--ink-2)]">Project progress: <b className="text-[color:var(--ink)]">{Math.round((project.progress ? Object.values(project.progress).reduce((a, b) => a + Number(b || 0), 0) / Math.max(Object.keys(project.progress).length, 1) : 0) * 100)}%</b></span>
            </>
          )}
        </div>
      </div>

      {/* Engagement Cards */}
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">Engagements</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {engagements.map((e) => (
            <div key={e.key} className="hairline bg-white p-5" data-testid={`engagement-${e.key}`}>
              <div className="flex items-baseline gap-3 flex-wrap mb-3">
                <span className="serif text-2xl tracking-tight">{e.country}</span>
                <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)]">
                  {e.iso}{e.rolv ? ` · ${e.rolv}` : ""}
                </span>
                {e.hub && <span className="mono text-[10px] px-1.5 py-0.5 border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>{e.hub}</span>}
                {e.cluster && <span className="mono text-[10px] px-1.5 py-0.5 border border-[color:var(--border)] text-[color:var(--ink-2)]">{e.cluster}</span>}
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <Kv label="Organizations" val={(e.organizations || []).join(", ") || "—"} />
                <Kv label="Involvement" val={(e.engagement_functions || []).join(" · ") || "—"} />
                <Kv label="Activities" val={(e.activities || []).join(", ") || "—"} />
                <Kv label="Goals" val={(e.translation_goals || []).join(", ") || "—"} />
                <Kv label="Status" val={(e.statuses || []).join(", ") || "—"} />
                <Kv label="Dates" val={`${e.actual_begin || e.planned_begin || "—"} → ${e.actual_end || e.planned_end || "—"}`} />
              </div>
              {(e.current_scope?.length || e.completed_products?.length) ? (
                <div className="border-t border-[color:var(--border)] mt-4 pt-3 text-xs">
                  {e.current_scope?.length > 0 && (
                    <div className="mb-2">
                      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mb-1">Current Scope</div>
                      <div className="flex flex-wrap gap-1">
                        {e.current_scope.map((s) => (
                          <span key={s} className="mono px-1 py-0.5 border rounded-sm" style={{ fontSize: "9px", borderColor: "var(--accent)", color: "var(--accent)" }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {e.completed_products?.length > 0 && (
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mb-1">Completed Products</div>
                      <div className="flex flex-wrap gap-1">
                        {e.completed_products.map((p, i) => (
                          <span key={i} className="chip inline-flex items-center gap-1" style={{ borderColor: "var(--on-track)", color: "var(--on-track)" }}>
                            <PackageCheck size={10} />
                            {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.product}</a> : p.product}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Notes + Issues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FeedColumn title="Notes" icon={StickyNote} accent="var(--on-track)" entries={notes} emptyLabel="No notes yet across any country for this language." />
        <FeedColumn title="Issues" icon={AlertTriangle} accent="var(--amber)" entries={issues} emptyLabel="No issues raised across any country for this language." />
      </div>
    </div>
  );
}

function Kv({ label, val }) {
  return (
    <div>
      <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">{label}</div>
      <div className="mt-0.5">{val}</div>
    </div>
  );
}

function FeedColumn({ title, icon: Icon, accent, entries, emptyLabel }) {
  return (
    <div className="hairline bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} style={{ color: accent }} strokeWidth={2} />
        <span className="serif text-xl">{title}</span>
        <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] ml-1">({entries.length})</span>
      </div>
      {entries.length === 0 ? (
        <div className="text-sm text-[color:var(--ink-2)] italic">{emptyLabel}</div>
      ) : (
        <div className="space-y-3">
          {entries.map((it, i) => (
            <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: accent }}>
              <div className="text-sm whitespace-pre-wrap">{it.text}</div>
              <div className="mono text-[10px] text-[color:var(--ink-3)] mt-1">
                {it.user} · {new Date(it.created_at).toLocaleString()}
                {it.country && <> · <MapPin size={9} className="inline" /> {it.country}{it.rolv ? ` · ${it.rolv}` : ""}</>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
